import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';

import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { capitalizeFirstLetter, calculateLocalDOB, SOCKET_EVENTS } from '../shared';
import { usePredictionSocket } from "../shared/hooks/usePredictionSocket";
import socketManager from '../utils/socketManager';
import MarkdownMessage from './mark-down/MarkdownMessage';

const ChatBot = ({ currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { connected, connectionStatus, emit, on, off } = usePredictionSocket(currentUser?._id);
  const [isLoading, setIsLoading] = useState(false);

  const streamingMessageIdRef = useRef(null);

  useEffect(() => {
    // Socket manager state tracking
  }, [connected, connectionStatus]);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (connected) {
    }
  }, [connected]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Socket event listeners
  useEffect(() => {
    if (!connected) {
      return;
    }

    // Listen for question:received (main response handler)
    const _handleQuestionReceived = (data) => {
      let botMessage = null;

      // Handle different response types
      if (data.followup_question) {
        // This is a follow-up question
        botMessage = {
          id: Date.now(),
          text: data.followup_question,
          isBot: true,
          timestamp: new Date(),
          isFollowUp: true,
        };
      } else if (data.refined_prediction) {
        // This is a refined prediction
        botMessage = {
          id: Date.now(),
          text: data.refined_prediction,
          isBot: true,
          timestamp: new Date(),
          isPrediction: true,
        };
      } else if (data.predictions && data.predictions.length > 0) {
        // This contains raw predictions
        const predictionText = data.predictions.map(pred => pred.prediction).join('\n\n');
        botMessage = {
          id: Date.now(),
          text: predictionText,
          isBot: true,
          timestamp: new Date(),
          isPrediction: true,
        };
      } else {
        // Fallback for any other response
        botMessage = {
          id: Date.now(),
          text: data.message || data.text || "I received your message but couldn't process it properly.",
          isBot: true,
          timestamp: new Date(),
        };
      }


      if (botMessage) {
        setMessages(prev => [...prev, botMessage]);
      }

      // Stop loading when response is received
      setIsLoading(false);
    };

    const handleQuestionReceived = (data) => {

      const chunkText = data.message;

      if (!chunkText || !chunkText.trim()) return;

      if (!streamingMessageIdRef.current) {

        const messageId = Date.now();
        streamingMessageIdRef.current = messageId;

        setMessages(prev => [
          ...prev,
          {
            id: messageId,
            text: chunkText,
            isBot: true,
            isStreaming: !data.isComplete,
            timestamp: new Date(),
          },
        ]);

      } else {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.id === streamingMessageIdRef.current
              ? { ...msg, text: msg.text + chunkText, isStreaming: !data.isComplete }
              : msg
          );
          return updatedMessages;
        });
      }
      if (data.isComplete) {
        // streamingMessageIdRef.current = null;
        // setIsLoading(false);
      }
      setIsLoading(false);
    };



    // Listen for errors
    const handleError = (error) => {
      console.error('❌ Socket error:', error);
      const botMessage = {
        id: Date.now(),
        text: error.message || "Sorry, I encountered an error. Please try again.",
        isBot: true,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, botMessage]);
      setIsLoading(false);
    };

    // Use DIRECT socket listeners instead of the hook's event system
    if (socketManager.socket) {
      // socketManager.socket.on('question:received', handleQuestionReceived);
      socketManager.socket.on(SOCKET_EVENTS.ADMIN_QUESTION_RESPONSE, handleQuestionReceived);
      socketManager.socket.on('error', handleError);
    }

    // Cleanup function
    return () => {
      if (socketManager.socket) {
        // socketManager.socket.off('question:received', handleQuestionReceived);
        socketManager.socket.off(SOCKET_EVENTS.ADMIN_QUESTION_RESPONSE, handleQuestionReceived);
        socketManager.socket.off('error', handleError);
      }
    };
  }, [connected]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    if (lastMessage && !lastMessage.isStreaming && streamingMessageIdRef.current) {
      streamingMessageIdRef.current = null;
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputMessage || !inputMessage.trim() || isLoading) {
      return;
    }

    // Store the question text before clearing input
    const questionText = inputMessage.trim();

    // Create the user message
    const userMessage = {
      id: Date.now(),
      text: questionText,
      isBot: false,
      timestamp: new Date(),
    };

    // Update messages and clear input
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    // Send to socket if connected
    if (connected) {
      const questionData = {
        message: questionText,
      };


      // Start loading
      setIsLoading(true);

      // emit("ask:question", questionData);
      emit(SOCKET_EVENTS.ADMIN_QUESTION, questionData);
    } else {
      // Add a bot message indicating no connection
      const botMessage = {
        id: Date.now() + 1,
        text: "⚠️ Not connected to server. Message saved but not sent.",
        isBot: true,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <motion.div
          style={{
            position: 'fixed',
            bottom: '40px',
            right: '40px',
            zIndex: 1000,
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <IconButton
            onClick={() => setIsOpen(true)}
            sx={{
              width: 60,
              height: 60,
              bgcolor: 'transparent',
              color: 'white',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              border: 'none',
              borderRadius: '50%',
              padding: 0,
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0 6px 25px rgba(0, 0, 0, 0.3)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <img
              src="https://stage-image-upload-service.s3.ap-south-1.amazonaws.com/uploads/1757483814757_Icon-App-1024x1024@1x.png"
              alt="Ay Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </IconButton>
        </motion.div>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.1,
              x: 100,
              y: 100,
              transformOrigin: "bottom right"
            }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
              transformOrigin: "bottom right"
            }}
            exit={{
              opacity: 0,
              scale: 0.1,
              x: 100,
              y: 100,
              transformOrigin: "bottom right"
            }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
              scale: { type: "spring", stiffness: 300, damping: 30 }
            }}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '50px',
              width: '380px',
              height: '500px',
              zIndex: 999,
            }}
          >
            <Paper
              elevation={10}
              sx={{
                height: '100%',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#4f46e5',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'transparent',
                    width: 40,
                    height: 40,
                    border: 'none',
                    borderRadius: '50%',
                    padding: 0,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src="https://stage-image-upload-service.s3.ap-south-1.amazonaws.com/uploads/1757483814757_Icon-App-1024x1024@1x.png"
                    alt="Ay Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentUser ? capitalizeFirstLetter(currentUser.name || currentUser.email || "User") : "User"}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {currentUser && currentUser.birthDetails?.date
                        ? (() => {
                          const dob = calculateLocalDOB(
                            currentUser.birthDetails.date,
                            currentUser.birthDetails.utcOffset
                          );
                          return dob ? dob.split(',')[0] : "Unknown DOB";
                        })()
                        : "Unknown DOB"
                      }
                    </Typography>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: connected ? "#10b981" : "#ef4444",
                      }}
                    />
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                      {connected ? "Connected" : "Disconnected"}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setIsOpen(false)}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Messages Container */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: 'white',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {messages.length === 0 && (
                  <Box sx={{ textAlign: 'center', color: '#6b7280', mt: 2 }}>
                    <Typography variant="body2">
                      Start a conversation by sending a message!
                    </Typography>
                  </Box>
                )}
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: message.isBot ? 'flex-start' : 'flex-end',
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '80%',
                          display: 'flex',
                          flexDirection: message.isBot ? 'row' : 'row-reverse',
                          alignItems: 'flex-end',
                          gap: 1,
                        }}
                      >
                        {message.isBot && (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: 'transparent',
                              border: 'none',
                              borderRadius: '50%',
                              padding: 0,
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src="https://stage-image-upload-service.s3.ap-south-1.amazonaws.com/uploads/1757483814757_Icon-App-1024x1024@1x.png"
                              alt="Ay Logo"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                          </Avatar>
                        )}

                        <Box>
                          <Paper
                            sx={{
                              p: 1.5,
                              bgcolor: message.isBot
                                ? '#f3f4f6'
                                : '#4f46e5',
                              color: message.isBot
                                ? '#1f2937'
                                : 'white',
                              borderRadius: message.isBot
                                ? '16px 16px 16px 4px'
                                : '16px 16px 4px 16px',
                              maxWidth: '100%',
                              border: message.isFollowUp ? '1px solid #f59e0b' : message.isPrediction ? '1px solid #10b981' : message.isError ? '1px solid #ef4444' : 'none',
                            }}
                          >
                            {message.isFollowUp && (
                              <Typography variant="caption" sx={{
                                display: 'block',
                                fontWeight: 'bold',
                                mb: 0.5,
                                color: '#92400e'
                              }}>
                              </Typography>
                            )}
                            {message.isPrediction && (
                              <Typography variant="caption" sx={{
                                display: 'block',
                                fontWeight: 'bold',
                                mb: 0.5,
                                color: '#e1f1edff'
                              }}>
                              </Typography>
                            )}
                            {message.isError && (
                              <Typography variant="caption" sx={{
                                display: 'block',
                                fontWeight: 'bold',
                                mb: 0.5,
                                color: '#eee3e3ff'
                              }}>
                                ⚠️ Error
                              </Typography>
                            )}

                            {/* <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-line',
                                lineHeight: 1.4,
                              }}
                            >
                              {message.text}
                            </Typography> */}
                            <MarkdownMessage text={message.text} />
                          </Paper>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6b7280',
                              mt: 0.5,
                              display: 'block',
                              textAlign: message.isBot ? 'left' : 'right',
                            }}
                          >
                            {formatTime(message.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                ))}

                {/* Loading Indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '80%',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          gap: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'transparent',
                            border: 'none',
                            borderRadius: '50%',
                            padding: 0,
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src="https://stage-image-upload-service.s3.ap-south-1.amazonaws.com/uploads/1757483814757_Icon-App-1024x1024@1x.png"
                            alt="Ay Logo"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                        </Avatar>
                        <Box>
                          <Paper
                            sx={{
                              p: 1.5,
                              bgcolor: '#f3f4f6',
                              color: '#1f2937',
                              borderRadius: '16px 16px 16px 4px',
                              maxWidth: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <CircularProgress size={16} thickness={5} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: 'italic',
                                color: '#6b7280'
                              }}
                            >
                              Thinking...
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </Box>

              <Divider />

              {/* Message Input */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-end',
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  variant="outlined"
                  size="small"
                  // disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      bgcolor: '#f9fafb',
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4f46e5',
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  sx={{
                    bgcolor: connected && !isLoading ? '#4f46e5' : '#6b7280',
                    color: 'white',
                    width: 40,
                    height: 40,
                    '&:hover': {
                      bgcolor: connected && !isLoading ? '#4338ca' : '#4b5563',
                    },
                    '&:disabled': {
                      bgcolor: '#d1d5db',
                      color: '#9ca3af',
                    },
                  }}
                >
                  <SendIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;